import React from 'react'
import { Head, router } from '@inertiajs/react'
import SectionTitle from '../../components/SectionTitle'
import { Button } from '../../components/ui'
import { BadgeQuestionMark, Building, IdCard, Lock, ShieldCheck, UserCheck, UserLock, Users } from 'lucide-react'
import IconRound from '../../components/IconRound'
import logoQm from '../../assets/qualimetrie.png';
import { HowItWorks } from '../shared/components/HowItWorks';
import { handleEvaluationButtonClick } from '../../utils/redirectToNewReview'
const bullets = [
  {
    icon: UserCheck,
    text: "De vrais propriétaires",
  },
  {
    icon: ShieldCheck,
    text: "Données protégées",
  },
  {
    icon: BadgeQuestionMark,
    text: "Aucune influence de marque",
  }
]

const trustItems = [
  {
    icon: Users,
    title: "D'où proviennent les avis ?",
    description: "Tous les avis proviennent de propriétaires authentifiés grâce à la plaque d’immatriculation de véhicule donné.",
  },
  {
    icon: IdCard,
    title: "Pourquoi demandez-vous la plaque d'immatriculation ?",
    description: "La plaque d'immatriculation nous permet de vérifier l'existence du véhicule. Elle sert uniquement à des fins de validation et n'est jamais affichée publiquement ni partagée avec des tiers."
  },
  {
    icon: Lock,
    title: "Comment mes données sont-elles protégées ?",
    description: "Vos données sont protégées conformément au RGPD et ne sont jamais vendues ni partagées. Nous prenons la confidentialité très au sérieux et utilisons vos informations uniquement pour vérifier la propriété et améliorer la plateforme."
  }
]

type AboutProps = {
  title: string;
  icon: React.ReactNode;
  description: string;
}

const About: React.FC<AboutProps> = () => {
  return (
    <React.Fragment>
      <div className="mt-10 container mx-auto px-4">
        <Head title="Qui sommes-nous ? - Review car" />
        <h1 className="text-center lg:max-w-[75%] mx-auto">
          Des avis automobiles auxquels
          <br />
          vous pouvez <span className="primary-color">vraiment faire confiance</span>
        </h1>
        <div className="text-center mx-auto text-muted-foreground lg:max-w-[50%] mt-4">
          Des expériences authentiques de vrais conducteurs. 
          <br>
          </br>
          Des avis indépendants et transparents pour vous aider à faire le bon choix.
        </div>
        <div className="mt-6 flex flex-col lg:flex-row gap-2 lg:justify-center">
          <Button
            size="lg"
            onClick={() => handleEvaluationButtonClick({ page_type: "A propos" })}
          >
            Évaluer mon véhicule
          </Button>
          <Button
            variant="link"
            size="lg"
            onClick={() => router.visit('/about#how-it-works')}
          >
            Comment ca fonctionne ?
          </Button>
        </div>
        <div className="mt-4 lg:mt-12 flex gap-4 justify-between mx-auto lg:max-w-[50%]">
          {bullets.map((bullet) => (
            <IconRound
              key={bullet.text}
              icon={bullet.icon}
              text={bullet.text}
            />
          ))}
        </div>
      </div>
      <div className="mt-4 lg:mt-12 container mx-auto px-4">
        <SectionTitle
          title="Qui sommes-nous ?"
        />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mt-6 lg:max-w-[70%] lg:mx-auto lg:gap-16">
          <div className="col-span-7 flex gap-4">
            <div>
              <IconRound
                icon={Building}
              />
            </div>
            <div>
              <h3 className="text-lg font-bold mb-2">
                Indépendant et axé sur l'expertise
              </h3>
              <p className="mb-2">
                Review Car est une plateforme indépendante d'évaluation automobile, sans aucune influence des constructeurs. Nous faisons partie du <span className="primary-color font-semibold">Groupe Qualimetrie</span>, leader reconnu dans la mesure de l'expérience client et fort de plus de 35 ans d'expertise.
              </p>
              <p>
                Notre mission est simple : fournir aux conducteurs des avis authentiques et vérifiés sur lesquels ils peuvent se fier.
              </p>
            </div>
          </div>
          <div className="col-span-5">
            <img
              src={logoQm}
              width={300}
              alt="Image"
              className="rounded-lg"
            />
          </div>
        </div>
      </div>
      <div className="mt-4 lg:mt-12">
        <HowItWorks />
      </div>
      <div className="mt-4 lg:mt-12 mb-4">
        <h2 className="text-center">
          Pourquoi faire confiance à  Review Car ?
        </h2>
        <p className="text-muted-foreground text-center mt-2">
          La transparence est notre fondement. Voici comment nous travaillons.
        </p>
        <div className="flex flex-col gap-6 mx-auto lg:max-w-[70%] px-4 mt-4">
          {trustItems.map((item) => (
            <div key={item.title} className="shadow-lg p-6 flex items-start gap-6 rounded-lg">
              <div className="text-center">
                {<item.icon className="size-6 primary-color" />}
              </div>
              <div>
                <h3 className="text-lg font-bold mb-2">
                  {item.title}
                </h3>
                <p className="text-muted-foreground">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </React.Fragment>
  )
}

export default About